<template>
  <data-loader>
    <div class="sim-start-container">
      <div class="sim-start-content">
        <screen-size-warning></screen-size-warning>
        <div
          v-html="html"
        >
        </div>
        <div class="button-wrapper">
          <data-loader
            :check-route="true"
          >
            <button
              class="sim-start-button-primary"
              data-test="start-simulation-button"
              @click="startSimulation"
            >
              {{ isMultiSectionSimulation ? 'Start' : 'Continue' }}
            </button>
          </data-loader>
        </div>
        <div
          v-if="isAdaptiveSimulation && pastAttempts.length"
          class="past-attempts"
          data-test="adaptive-past-attempts"
        >
          <h4>Your past results</h4>
          <router-link
            v-for="attempt in pastAttempts"
            :key="attempt.id"
            class="past-attempt"
            :to="{ name: 'adaptiveSim#result', params: { simId: simulation.id, sessionId: attempt.id } }"
          >
            <span>{{ formatDate(attempt.finishedAt) }}</span>
            <span>{{ attempt.itemsAnswered }} questions</span>
            <span :class="attempt.result === 'pass' ? 'passed' : 'failed'">{{ attempt.result === 'pass' ? 'Passed' : 'Did not pass' }}</span>
          </router-link>
        </div>
      </div>
    </div>
  </data-loader>
</template>

<script>
import ScreenSizeWarning from '@/components/sims/ScreenSizeWarning.vue';
import DataLoader from '@/components/sims/DataLoader.vue';
import WuxSimulationsMixin from '@/mixins/wux-simulations';

export default {
  components: {
    ScreenSizeWarning,
    DataLoader,
  },
  mixins: [WuxSimulationsMixin],
  computed: {
    // Finished adaptive attempts, newest first
    pastAttempts() {
      return (this.$store.getters['adaptiveSim/history'](this.simId) || []).filter(attempt => attempt.status === 'completed');
    },
    html() {
      if (this.isMultiSectionSimulation) {
        return this.simulation.introText || 'Intro text missing, configured on the category for this simulation.';
      } else if (this.isSimulation) {
        return this.simConfig?.startHtml || 'Intro text missing, configured in the simulation config for this category.';
      }
      return 'This category is not a simulation.';
    },
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '';
      return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(timestamp));
    },
    startSimulation() {
      if (this.isMultiSectionSimulation) {
        return this.startMultiSectionSimulation();
      } else if (this.isSimulation) {
        return this.startSingleSectionSimulation();
      }
      return this.$router.push({ name: 'home' });
    },
    startMultiSectionSimulation() {
      const childrenIds = this.simulation?.children || [];
      const sections = this.$store.getters['categories/all'].
        filter( c => childrenIds.includes(c.id) ).
        sort( (a, b) => a.number - b.number );

      const params = {
        number: 0,
        categoryStatusId: this.notStartedStatus.id,
        categoryStatusName: this.notStartedStatus.name,
        categoryId: sections[0].id,
        mobileCreatedAt: Date.now(),
      };
      this.$analytics.events.simulationCategoryState(params);
      this.$analytics.events.simulationStart({
        simulationId: this.simulation.id,
        simulationName: this.simulation.name,
      });
      return this.$store.dispatch('categoryStates/sendCategoryState', params).then(() => {
        return this.$router.push({
          name: 'simSection#show',
          params: {
            simId: this.simulation.id,
            sectionId: sections[0].id,
          },
        });
      });
    },
    startSingleSectionSimulation() {
      // Adaptive attempts start on the last intro page, without category states
      if (this.isAdaptiveSimulation) {
        return this.$router.push({ name: 'sims#intro', params: { simId: this.simulation.id, pageNum: '1' } });
      }

      const params = {
        number: 0,
        categoryStatusId: this.notStartedStatus.id,
        categoryStatusName: this.notStartedStatus.name,
        categoryId: this.simulation.id,
        mobileCreatedAt: Date.now(),
      };

      this.$analytics.events.simulationCategoryState(params);
      return this.$store.dispatch('categoryStates/sendCategoryState', params).then(() => {
        return this.$router.push({
          name: 'sims#intro',
          params: {
            simId: this.simulation.id,
            pageNum: '1',
          },
        });
      });

    },
  },
};
</script>

<style lang="scss" scoped>
.sim-start-container {
  display: grid;
  place-items: center;
  height: 100%;
  background-color: #edf1f5;
}
.sim-start-content {
  max-width: 800px;
  display: inline-block;
  box-shadow: 0 0 10px 0 lightgray;
  padding: 2rem;
  border-radius: .5rem;
  background-color: white;
}

.past-attempts {
  margin-top: 2rem;
  border-top: 1px solid #d9d9d9;
  padding-top: 1rem;

  h4 {
    margin: 0 0 .5rem;
  }

  .past-attempt {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: .5rem 0;
    color: inherit;
    border-bottom: 1px solid #edf1f5;

    &:hover {
      background: #f7f9fb;
    }
  }

  .passed {
    color: #00b46d;
    font-weight: 600;
  }

  .failed {
    color: #dc3416;
    font-weight: 600;
  }
}

.button-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 2rem;

  .sim-start-button-primary {
    border-radius: 4px;
    box-sizing: border-box;
    color: white;
    font-size: rem-calc(18);
    font-weight: $semi-bold;
    margin-top: rem-calc(16);
    padding: rem-calc(6);
    text-align: center;
    width: rem-calc(150);
    height: rem-calc(46);
    background-color: #7bd56e;
    border: solid 1px #58AE4D;
  }
}
</style>>
